import React, { useEffect } from 'react';
import { COLOR_SCHEMES, getColorSchemesForExam, getPearsonColorSchemeDropdownLabel } from '../constants/tmuaColorSchemes';
import TmuaPearsonShell, {
  DoorIcon,
  NextIcon,
  TmuaPearsonFooterButton,
  useTmuaPearsonAccessKeys,
} from './TmuaPearsonShell';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';

/**
 * UCAT NDA / exam-start screen shown after the welcome confirm (Yes).
 * Layout and copy match the official Practice Test reference; colours use
 * IVORI / COLOR_SCHEMES rather than Pearson blue.
 */

const UcatLogo = () => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'stretch',
      flexShrink: 0,
      fontFamily: 'var(--iv-font-body)',
      userSelect: 'none',
    }}
    aria-label="UCAT University Clinical Aptitude Test"
  >
    <div
      style={{
        background: '#7A1F2B',
        color: '#FFFFFF',
        fontWeight: 800,
        fontSize: '22px',
        letterSpacing: '0.04em',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      UCAT
    </div>
    <div
      style={{
        background: '#1A2332',
        color: '#FFFFFF',
        fontWeight: 600,
        fontSize: '11px',
        letterSpacing: '0.06em',
        lineHeight: 1.25,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
        maxWidth: '160px',
      }}
    >
      UNIVERSITY CLINICAL APTITUDE TEST
    </div>
  </div>
);

/** Pearson-chrome branch: TmuaPearsonShell wrapper, End Exam + Next footer, no logo, rewritten notice. */
const UcatPearsonNdaScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'pearsonDefault',
  setColorScheme,
  onNext,
  onEndExam,
}) => {
  const T = TMUA_PEARSON_TOKENS;

  useTmuaPearsonAccessKeys(
    { nextAvailable: true, endExamAvailable: true },
    { onNext, onEndExam }
  );

  return (
    <TmuaPearsonShell
      examName="UCAT"
      title={paperTitle}
      colorScheme={colorScheme}
      setColorScheme={setColorScheme}
      colorSchemeLabel={getPearsonColorSchemeDropdownLabel('UCAT')}
      onBackToSelection={onEndExam}
      footerLeft={(
        <TmuaPearsonFooterButton onClick={onEndExam} icon={<DoorIcon color={T.titleBarFg} />} underline="e" keepLabelOnMobile>
          End Exam
        </TmuaPearsonFooterButton>
      )}
      footerRight={(
        <TmuaPearsonFooterButton variant="chrome" onClick={onNext} icon={<NextIcon color="#FFFFFF" />} iconPosition="end" underline="n">
          Next
        </TmuaPearsonFooterButton>
      )}
    >
      <div className="tmua-pearson-instruction-copy" style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: '18px 22px', color: '#000', fontFamily: T.fontUi, fontSize: 16, lineHeight: 1.35 }}>
        <div style={{ maxWidth: '920px' }}>
          <p style={{ margin: '0 0 12px' }}>
            Welcome to the <strong>UCAT</strong> practice sitting.
          </p>
          <p style={{ margin: '0 0 12px', fontWeight: 700 }}>Practice notice:</p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 22 }}>
            <li style={{ marginBottom: 8 }}>
              This is a practice sitting for your own preparation. Please do not share, copy, or redistribute the
              questions or your answers.
            </li>
            <li style={{ marginBottom: 8 }}>
              Your results in this practice sitting are not saved or reported anywhere.
            </li>
          </ul>
          <p style={{ margin: 0 }}>
            Click the{' '}
            <strong>
              Next (<span style={{ textDecoration: 'underline' }}>N</span>)
            </strong>{' '}
            button when you are ready to begin.
          </p>
        </div>
      </div>
    </TmuaPearsonShell>
  );
};

const UcatCreamNdaScreen = ({
  paperTitle = 'Practice Test',
  colorScheme = 'ivori',
  setColorScheme,
  onNext,
  onEndExam,
}) => {
  const schemeOptions = getColorSchemesForExam('UCAT');
  const scheme = schemeOptions[colorScheme] || COLOR_SCHEMES.ivori;
  const ink = scheme.text || 'var(--iv-head)';
  const divider = colorScheme !== 'ivori' ? scheme.border : 'var(--iv-line)';

  const bodyText = {
    fontSize: '15px',
    lineHeight: 1.6,
    color: ink,
    margin: '0 0 16px',
    fontFamily: 'var(--iv-font-body)',
  };

  const bulletList = {
    margin: '0 0 16px',
    paddingLeft: '22px',
    fontSize: '15px',
    lineHeight: 1.6,
    color: ink,
    fontFamily: 'var(--iv-font-body)',
    listStyleType: 'disc',
    listStylePosition: 'outside',
  };

  const bulletItem = {
    marginBottom: '8px',
    display: 'list-item',
    listStyleType: 'disc',
    color: ink,
  };

  const footerButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: ink,
    background: scheme.chrome,
    border: `1.5px solid ${scheme.border}`,
    borderRadius: '4px',
    padding: '8px 20px',
    cursor: 'pointer',
    fontFamily: 'var(--iv-font-body)',
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.repeat || event.isComposing) return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'select' || tag === 'input' || tag === 'textarea') return;
      if (event.ctrlKey || event.metaKey) return;

      const isN = event.key === 'n' || event.key === 'N';
      if (!isN) return;
      event.preventDefault();
      onNext?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNext]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: scheme.body,
        color: ink,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--iv-font-body)',
      }}
    >
      {/* Title bar */}
      <div
        style={{
          padding: '12px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: 700, color: ink }}>
          {paperTitle}
        </span>
      </div>

      {/* Colour scheme bar */}
      <div
        style={{
          padding: '8px 28px',
          background: scheme.chrome,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <select
          value={schemeOptions[colorScheme] ? colorScheme : 'ivori'}
          onChange={(e) => setColorScheme?.(e.target.value)}
          aria-label="Colour Scheme"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: ink,
            fontFamily: 'var(--iv-font-body)',
            background: scheme.chrome,
            border: `1.5px solid ${scheme.border}`,
            borderRadius: '4px',
            padding: '5px 10px',
            cursor: 'pointer',
            minWidth: '160px',
          }}
        >
          {Object.entries(schemeOptions).map(([key, s]) => (
            <option key={key} value={key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '28px 40px 40px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <UcatLogo />
        </div>

        <div style={{ maxWidth: '920px' }}>
          <p style={bodyText}>
            Welcome to the <strong>UCAT</strong> exam.
          </p>
          <p style={bodyText}>
            If your name is incorrect, raise your hand for the invigilator.
          </p>
          <p style={bodyText}>
            If you choose to proceed you acknowledge that you will be monitored at all times and both audio and video may be recorded. If you have questions about this policy, raise your hand for the invigilator.
          </p>
          <p style={bodyText}>
            <strong>Non-disclosure agreement:</strong>
          </p>
          <ul style={bulletList}>
            <li style={bulletItem}>
              You are expressly prohibited from disclosing, publishing, reproducing or transmitting this exam, in whole or in part, in any form or by any means including visual, aural, verbal, written, electronic or mechanical means, for any purpose, without the prior express written permission of the UCAT Consortium.
            </li>
            <li style={bulletItem}>
              All examination contents are the published, confidential and proprietary materials of UCAT.
            </li>
            <li style={bulletItem}>
              All copyright is owned by the UCAT Consortium.
            </li>
          </ul>
          <p style={bodyText}>
            Please begin your test promptly. To keep your test on schedule, the invigilator may prompt you to start.
          </p>
          <p style={{ ...bodyText, marginBottom: 0 }}>
            Click the{' '}
            <strong>
              Next (<span style={{ textDecoration: 'underline' }}>N</span>)
            </strong>{' '}
            button when you are ready to begin the exam.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 28px',
          borderTop: `1px solid ${divider}`,
          background: scheme.chrome,
        }}
      >
        <button type="button" onClick={onEndExam} style={footerButtonStyle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16,17 21,12 16,7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          End Exam
        </button>
        <button type="button" onClick={onNext} style={footerButtonStyle}>
          <span>
            <span style={{ textDecoration: 'underline' }}>N</span>ext
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="13,6 19,12 13,18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/** Dispatcher: Pearson chrome iff pearsonSitting; else today's cream chrome (unchanged for orphans/fail-closed). */
const UcatNdaScreen = ({ pearsonSitting = false, ...rest }) => (
  pearsonSitting
    ? <UcatPearsonNdaScreen {...rest} />
    : <UcatCreamNdaScreen {...rest} />
);

export default UcatNdaScreen;
